import {
    createEmptyUser,
    FormTextField,
    IUser,
    List,
    SearchField,
    SectionLabel,
    SelectField,
    Spinner,
    StringUtils,
    useConfirmContext,
    UserRole,
} from '@localina/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, UserRoleMappingListItem } from '../../components';
import { availableRoles, NO_ACCESS } from '../../components/ListItem/UserRoleMappingListItem';
import { countRoles, setUserRoles } from '../../utils/UserRoleUtils';
import { useGetRestaurants } from '../../api/queries/restaurants';
import { useGetAccount } from '../../api/queries/account';
import { useCreateUser, useDeleteUser, useUpdateUser, useUser } from '../../api/queries/users';
import * as yup from 'yup';
import { Schema } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface IUserViewProps {
    open: boolean;
    onClose: () => void;
    userId: string;
}

type IUserForm = Omit<IUser, 'id' | 'rolesMapping'>;

const UserView = ({ ...props }: IUserViewProps) => {
    const { t } = useTranslation();
    const { snackbar, confirm } = useConfirmContext();
    const userId: string = props.userId;
    const isEditing: boolean = userId !== 'new';
    const [user, setUser] = React.useState<IUser | undefined>(undefined);
    const [venueFilter, setVenueFilter] = React.useState('');

    const createUserMutation = useCreateUser();
    const updateUserMutation = useUpdateUser();
    const deleteUserMutation = useDeleteUser();
    const accountQuery = useGetAccount();

    const userQuery = useUser(userId, { enabled: isEditing });
    const restaurantsQuery = useGetRestaurants();

    const isLoading: boolean =
        userQuery.isInitialLoading ||
        createUserMutation.isLoading ||
        updateUserMutation.isLoading ||
        deleteUserMutation.isLoading;

    const userSchema: Record<keyof IUserForm, Schema> = {
        firstName: yup.string().required().min(2).max(40),
        lastName: yup.string().required().min(2).max(40),
        email: yup
            .string()
            .test(
                'isValidEmail',
                t('core:errors.invalidEmail'),
                StringUtils.isValidEmail as yup.TestFunction<string | undefined, any>,
            )
            .required(),
    };

    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(yup.object().shape(userSchema)),
        defaultValues: {
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
        },
    });

    const { isDirty, isValid } = methods.formState;

    React.useEffect(() => {
        setUser(userQuery.data ? userQuery.data : createEmptyUser());
    }, [userQuery.data]);

    React.useEffect(() => {
        if (props.open) {
            const propUser = userQuery.data ? userQuery.data : createEmptyUser();
            methods.reset({
                firstName: propUser.firstName,
                lastName: propUser.lastName,
                email: propUser.email,
            });
        }
    }, [props.open]);

    const displaySuccessSnackbar = () => {
        snackbar({ msg: t('user.saved'), severity: 'success' });
    };

    const displayErrorSnackbar = (deleteRequested = false) => {
        snackbar({ msg: t(`user.errors.${deleteRequested ? 'delete' : 'save'}`), severity: 'error' });
    };

    const handleSave = () => {
        if (user && methods.getValues()) {
            const updatedUser = {
                ...user,
                firstName: methods.getValues().firstName,
                lastName: methods.getValues().lastName,
                email: methods.getValues().email,
            };

            const request: IUser = {
                ...updatedUser,
                rolesMapping: (restaurantsQuery.data?.restaurants || []).map((restaurant) => {
                    return {
                        restaurantId: restaurant.id,
                        roles: updatedUser.rolesMapping.find((rm) => rm.restaurantId === restaurant.id)?.roles || [],
                    };
                }),
            };

            if (isEditing) {
                updateUserMutation.mutate(request, {
                    onError: () => {
                        displayErrorSnackbar();
                    },
                    onSuccess: displaySuccessSnackbar,
                });
            } else {
                createUserMutation.mutate(request, {
                    onError: () => {
                        displayErrorSnackbar();
                    },
                    onSuccess: displaySuccessSnackbar,
                });
            }

            setUser(updatedUser);
            props.onClose();
        }
    };

    const handleDelete = async () => {
        if (isEditing) {
            if (
                (await confirm({
                    msg: t('user.dialog.message', { name: user?.email }),
                    title: t('user.dialog.title'),
                })) === 'yes' &&
                user
            ) {
                deleteUserMutation.mutate(user, {});
            }
            props.onClose();
        }
    };

    const confirmCloseDrawer = async () => {
        if (!isDirty || (await confirm({ title: t('staffTags.form.confirmDiscardChanges.title') })) === 'yes') {
            props.onClose();
        }
    };

    const assignableRestaurants = (restaurantsQuery.data?.restaurants ?? []).filter(
        (restaurant) => accountQuery.isSuccess && accountQuery.data.hasFeature('users', restaurant.id),
    );

    const needle = venueFilter.trim().toLowerCase();
    const visibleRestaurants = needle
        ? assignableRestaurants.filter((restaurant) =>
              [restaurant.info.name, restaurant.info.street, restaurant.info.city]
                  .filter(Boolean)
                  .some((field) => field.toLowerCase().includes(needle)),
          )
        : assignableRestaurants;

    const bulkRoles = ([UserRole.ADMIN, UserRole.SERVICE, UserRole.SERVER, UserRole.RECEPTION] as UserRole[]).filter(
        (role) =>
            visibleRestaurants.length > 0 &&
            visibleRestaurants.every((restaurant) =>
                availableRoles(
                    restaurant.id,
                    accountQuery.isSuccess ? accountQuery.data.hasFeature : undefined,
                ).includes(role),
            ),
    );

    const handleBulkRole = (value: string) => {
        if (!user || !value) {
            return;
        }
        setUser(
            setUserRoles(
                user,
                visibleRestaurants.map((restaurant) => restaurant.id),
                value === NO_ACCESS ? undefined : (value as UserRole),
            ),
        );
    };

    const summary = user
        ? countRoles(
              user,
              assignableRestaurants.map((restaurant) => restaurant.id),
          )
        : undefined;

    const summaryParts = summary
        ? [
              ...Array.from(summary.counts.entries()).map(([role, count]) =>
                  t('user.summary.entry', { role: t(`user.roles.${role}`), count }),
              ),
              ...(summary.none > 0
                  ? [t('user.summary.entry', { role: t('user.roles.none'), count: summary.none })]
                  : []),
          ]
        : [];

    return (
        <>
            <Drawer
                open={props.open}
                onClose={confirmCloseDrawer}
                title={
                    isEditing && user
                        ? StringUtils.combineStrings([user.lastName, user.firstName], ', ') || t('user.title')
                        : t('user.title')
                }
                subtitle={
                    summary && assignableRestaurants.length > 0
                        ? t('user.accessSummary', {
                              count: assignableRestaurants.length - summary.none,
                              total: assignableRestaurants.length,
                          })
                        : undefined
                }
                disabled={!isValid}
                onSave={methods.handleSubmit(handleSave)}
                onDelete={user?.id ? handleDelete : undefined}
            >
                {user && (
                    <FormProvider {...methods}>
                        <form>
                            <FormTextField name={'firstName'} label={t('user.fields.firstName')} />
                            <FormTextField name={'lastName'} label={t('user.fields.lastName')} />
                            <FormTextField name={'email'} label={t('user.fields.email')} />
                            <div className="user__access">
                                {}
                                <SectionLabel
                                    value={t('user.restaurants')}
                                    action={
                                        bulkRoles.length > 0 ? (
                                            <div className="user__access__bulk">
                                                <SelectField
                                                    label={t('user.bulk.label')}
                                                    value=""
                                                    options={[
                                                        ...bulkRoles.map((role) => ({
                                                            value: role,
                                                            label: t(`user.roles.${role}`),
                                                        })),
                                                        { value: NO_ACCESS, label: t('user.roles.none') },
                                                    ]}
                                                    onChange={handleBulkRole}
                                                />
                                            </div>
                                        ) : undefined
                                    }
                                />

                                {assignableRestaurants.length > 4 && (
                                    <div className="user__access__filter">
                                        <SearchField
                                            label={t('user.venueFilter')}
                                            value={venueFilter}
                                            onChange={setVenueFilter}
                                            onSearch={setVenueFilter}
                                        />
                                    </div>
                                )}

                                <List>
                                    {visibleRestaurants.map((restaurant) => (
                                        <UserRoleMappingListItem
                                            key={restaurant.id}
                                            user={user}
                                            restaurant={restaurant}
                                            onChange={setUser}
                                        />
                                    ))}
                                </List>

                                {summaryParts.length > 0 && (
                                    <div className="user__access__summary">
                                        <span className="user__access__summary__title">{t('user.summary.title')}</span>
                                        <span className="user__access__summary__body">{summaryParts.join(' · ')}</span>
                                    </div>
                                )}
                            </div>
                        </form>
                    </FormProvider>
                )}
            </Drawer>
            {isLoading && <Spinner />}
        </>
    );
};

export default UserView;
